/**
 * Audit Log Service
 * Part of TASK-334: Frontend Admin Panel
 *
 * Handles audit log querying, filtering, exporting, and reports
 * for the admin panel.
 */

import { httpClient } from '../../lib/httpClient';
import type {
  AuditLogDetailResponse,
  AuditLogListResponse,
  AuditFilter,
  AuditExportOptions,
  AuditEventTypesResponse,
  UserActivityReportResponse,
  BlockedAccessReportResponse,
  LoginHistoryReportResponse,
} from '../../types/admin';

class AuditService {
  private readonly basePath = '/api/admin/audit';

  /**
   * Query audit logs with filtering and pagination
   */
  async listLogs(filter?: AuditFilter): Promise<AuditLogListResponse> {
    try {
      // Convert filter to query params
      const params: Record<string, string | number | undefined> = {};

      if (filter) {
        if (filter.user_id) params.user_id = filter.user_id;
        if (filter.event_type) params.event_type = filter.event_type;
        if (filter.event_types?.length) params.event_types = filter.event_types.join(',');
        if (filter.resource_type) params.resource_type = filter.resource_type;
        if (filter.resource_id) params.resource_id = filter.resource_id;
        if (filter.client_id) params.client_id = filter.client_id;
        if (filter.severity) params.severity = filter.severity;
        if (filter.date_from) params.date_from = filter.date_from;
        if (filter.date_to) params.date_to = filter.date_to;
        if (filter.ip_address) params.ip_address = filter.ip_address;
        if (filter.search) params.search = filter.search;
        if (filter.page) params.page = filter.page;
        if (filter.page_size) params.page_size = filter.page_size;
        if (filter.last_id) params.last_id = filter.last_id;
      }

      const data = await httpClient.get<AuditLogListResponse>(`${this.basePath}/logs`, { params });
      return data;
    } catch (error) {
      console.error('Error listing audit logs:', error);
      throw error;
    }
  }

  /**
   * Get a single audit log entry by ID
   */
  async getLog(logId: string): Promise<AuditLogDetailResponse> {
    try {
      const data = await httpClient.get<AuditLogDetailResponse>(`${this.basePath}/logs/${logId}`);
      return data;
    } catch (error) {
      console.error('Error fetching audit log:', error);
      throw error;
    }
  }

  /**
   * Export audit logs as CSV or JSON
   * Returns a blob for download
   */
  async exportLogs(options: AuditExportOptions): Promise<Blob> {
    try {
      const response = await httpClient.post(
        `${this.basePath}/logs/export`,
        options,
        {
          responseType: 'blob',
        }
      );
      return response as unknown as Blob;
    } catch (error) {
      console.error('Error exporting audit logs:', error);
      throw error;
    }
  }

  /**
   * Get available event types
   */
  async getEventTypes(): Promise<AuditEventTypesResponse> {
    try {
      const data = await httpClient.get<AuditEventTypesResponse>(`${this.basePath}/event-types`);
      return data;
    } catch (error) {
      console.error('Error fetching event types:', error);
      throw error;
    }
  }

  // ==================== Reports ====================

  /**
   * Get user activity report
   */
  async getUserActivityReport(params?: {
    days?: number;
    user_id?: string;
  }): Promise<UserActivityReportResponse> {
    try {
      const data = await httpClient.get<UserActivityReportResponse>(
        `${this.basePath}/reports/user-activity`,
        { params }
      );
      return data;
    } catch (error) {
      console.error('Error fetching user activity report:', error);
      throw error;
    }
  }

  /**
   * Get blocked access (Chinese wall violations) report
   */
  async getBlockedAccessReport(params?: {
    days?: number;
    user_id?: string;
    page?: number;
    page_size?: number;
  }): Promise<BlockedAccessReportResponse> {
    try {
      const data = await httpClient.get<BlockedAccessReportResponse>(
        `${this.basePath}/reports/blocked-access`,
        { params }
      );
      return data;
    } catch (error) {
      console.error('Error fetching blocked access report:', error);
      throw error;
    }
  }

  /**
   * Get login history report
   */
  async getLoginHistoryReport(params?: {
    days?: number;
    user_id?: string;
    include_failures?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<LoginHistoryReportResponse> {
    try {
      const data = await httpClient.get<LoginHistoryReportResponse>(
        `${this.basePath}/reports/login-history`,
        { params }
      );
      return data;
    } catch (error) {
      console.error('Error fetching login history report:', error);
      throw error;
    }
  }

  // ==================== Export Helpers ====================

  /**
   * Trigger download of exported logs
   */
  downloadExport(blob: Blob, format: 'csv' | 'json'): void {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const extension = format === 'csv' ? 'csv' : 'ndjson';
    const filename = `audit_logs_${timestamp}.${extension}`;

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Export and download logs in one call
   */
  async exportAndDownload(options: AuditExportOptions): Promise<void> {
    const blob = await this.exportLogs(options);
    this.downloadExport(blob, options.format);
  }

  // ==================== Formatting Helpers ====================

  /**
   * Format event type for display
   */
  formatEventType(eventType: string): string {
    return eventType
      .split('.')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).replace(/_/g, ' '))
      .join(' - ');
  }

  /**
   * Get event type category
   */
  getEventTypeCategory(eventType: string): string {
    return eventType.split('.')[0];
  }

  /**
   * Format timestamp for display
   */
  formatTimestamp(isoString: string): string {
    const date = new Date(isoString);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  /**
   * Format date for filter input
   */
  formatDateForInput(date: Date): string {
    return date.toISOString().slice(0, 16);
  }

  /**
   * Get date range presets for filtering
   */
  getDateRangePresets(): { label: string; days: number }[] {
    return [
      { label: 'Last 24 hours', days: 1 },
      { label: 'Last 7 days', days: 7 },
      { label: 'Last 30 days', days: 30 },
      { label: 'Last 90 days', days: 90 },
      { label: 'Last year', days: 365 },
    ];
  }

  /**
   * Apply date range preset to filter
   */
  applyDateRangePreset(days: number): { date_from: string; date_to: string } {
    const now = new Date();
    const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    return {
      date_from: from.toISOString(),
      date_to: now.toISOString(),
    };
  }

  /**
   * Parse changes object for display
   */
  parseChanges(changes: Record<string, unknown> | undefined): {
    key: string;
    oldValue: unknown;
    newValue: unknown;
  }[] {
    if (!changes) return [];

    return Object.entries(changes).map(([key, value]) => {
      if (typeof value === 'object' && value !== null && 'old' in value && 'new' in value) {
        return {
          key,
          oldValue: (value as { old: unknown; new: unknown }).old,
          newValue: (value as { old: unknown; new: unknown }).new,
        };
      }
      return {
        key,
        oldValue: undefined,
        newValue: value,
      };
    });
  }
}

export const auditService = new AuditService();
